import React, { useState } from "react";
import EmailButton from "@/components/common/EmailButton";

interface EmailPopupModalProps {
  isOpen: boolean;
  onClose: () => void;
  reportData?: {
    validationResults?: any[];
    validationSummary?: any;
    checkResults?: any[];
    currentViolations?: any[];
  };
  reportType?: string;
  pdfAttachment?: {
    filename: string;
    content?: string; // base64 encoded PDF content (optional)
    contentType?: string;
  };
  onNavigateToCelebration?: () => void;
}

export function EmailPopupModal({
  isOpen,
  onClose,
  reportData,
  reportType = "Compliance Report",
  pdfAttachment,
  onNavigateToCelebration,
}: EmailPopupModalProps) {
  const [userEmail, setUserEmail] = useState<string>("");
  const [emailSent, setEmailSent] = useState<boolean>(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailSuccess, setEmailSuccess] = useState<boolean>(false);
  const [selectedRecipients, setSelectedRecipients] = useState<string[]>([]);
  const [exportFormat] = useState<"pdf">("pdf");

  if (!isOpen) return null;

  // Generate report summary for email body
  const generateReportSummary = () => {
    if (!reportData) {
      return "No report data available.";
    }

    let summary = `Your Hillsdale Compliance Report is ready!\n\n`;

    if (
      reportData.validationResults &&
      reportData.validationResults.length > 0
    ) {
      // const total = reportData.validationResults.length;
      // // const passed = reportData.validationResults.filter(
      // //   (r: any) => r.status === "pass"
      // // ).length;
      // // const failed = reportData.validationResults.filter(
      // //   (r: any) => r.status === "fail"
      // // ).length;
      // // const warnings = reportData.validationResults.filter(
      // //   (r: any) => r.status === "warning"
      // // ).length;
      // // const passRate = total > 0 ? ((passed / total) * 100).toFixed(1) : "0";
      // // summary += `Report Summary:\n`;
      // // summary += `• Total Validations: ${total}\n`;
      // // summary += `• Passed: ${passed}\n`;
      // // summary += `• Failed: ${failed}\n`;
      // // summary += `• Warnings: ${warnings}\n`;
      // // summary += `• Pass Rate: ${passRate}%\n\n`;
    }

    if (reportData.checkResults && reportData.checkResults.length > 0) {
      const totalRules = reportData.checkResults.length;
      const passedRules = reportData.checkResults.filter(
        (r: any) => r.passed
      ).length;
      const failedRules = totalRules - passedRules;
      const complianceRate = (passedRules / totalRules) * 100;

      summary += `Compliance Check Results:\n`;
      summary += `• Total Rules Checked: ${totalRules}\n`;
      summary += `• Passed Rules: ${passedRules}\n`;
      summary += `• Failed Rules: ${failedRules}\n`;
      summary += `• Compliance Rate: ${complianceRate.toFixed(1)}%\n\n`;
    }

    if (
      reportData.currentViolations &&
      reportData.currentViolations.length > 0
    ) {
      summary += `Violations Found: ${reportData.currentViolations.length}\n`;

      // Group violations by severity
      const violationsBySeverity = reportData.currentViolations.reduce(
        (acc: any, v: any) => {
          const severity = v.severity?.toLowerCase() || "unknown";
          acc[severity] = (acc[severity] || 0) + 1;
          return acc;
        },
        {}
      );

      Object.entries(violationsBySeverity).forEach(([severity, count]) => {
        summary += `• ${severity.charAt(0).toUpperCase() + severity.slice(1)}: ${count}\n`;
      });
      summary += "\n";
    }

    summary += `The detailed report has been generated and is ready for download.\n\n`;

    // Add PDF filename information if available
    if (pdfAttachment?.filename) {
      summary += `PDF Report: ${pdfAttachment.filename}\n\n`;
    }

    summary += `Best regards,\nHeroCon.AI Team`;

    return summary;
  };

  const handleEmailSent = () => {
    setEmailSent(true);
    setEmailError(null);
    setEmailSuccess(true);

    // Auto-dismiss success after 3 seconds
    setTimeout(() => {
      setEmailSuccess(false);
    }, 3000);
  };

  const handleEmailError = (error: any) => {
    setEmailError(error.message || "Failed to send email. Please try again.");
    setEmailSent(false);

    // Auto-dismiss error after 5 seconds
    setTimeout(() => {
      setEmailError(null);
    }, 5000);
  };

  const handleClose = () => {
    setUserEmail("");
    setEmailSent(false);
    setEmailError(null);
    setEmailSuccess(false);
    setSelectedRecipients([]);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]">
      {/* Sticky Notifications */}
      {emailError && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-[10001] max-w-md w-full mx-4">
          <div className="bg-red-500 text-white px-6 py-4 rounded-lg shadow-lg border border-red-600 animate-slide-down">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <svg
                    className="w-5 h-5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-semibold">Email Failed</h4>
                  <p className="text-xs opacity-90 mt-1">{emailError}</p>
                </div>
              </div>
              <button
                onClick={() => setEmailError(null)}
                className="flex-shrink-0 ml-4 text-white hover:text-red-200 transition-colors"
              >
                <svg
                  className="w-4 h-4"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {emailSuccess && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-[10001] max-w-md w-full mx-4">
          <div className="bg-green-500 text-white px-6 py-4 rounded-lg shadow-lg border border-green-600 animate-slide-down">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <svg
                    className="w-5 h-5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-semibold">
                    Email Sent Successfully!
                  </h4>
                  <p className="text-xs opacity-90 mt-1">
                    Your compliance report has been sent with PDF attachment.
                  </p>
                </div>
              </div>
              <button
                onClick={() => setEmailSuccess(false)}
                className="flex-shrink-0 ml-4 text-white hover:text-green-200 transition-colors"
              >
                <svg
                  className="w-4 h-4"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-dark-100 rounded-xl border border-gray-700 p-6 max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col relative z-[10000]">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-semibold text-white flex items-center gap-2">
            <span className="text-3xl">📧</span>
            Email {reportType}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-white text-2xl"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Email Configuration */}
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700 mb-6">
            <h4 className="text-xl font-semibold text-gray-100 mb-4 flex items-center gap-2">
              <span className="text-green-400">📧</span>
              Email Configuration
            </h4>
            <div className="space-y-4">
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-300 mb-2"
                >
                  Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  placeholder="Enter your email address"
                  value={userEmail}
                  onChange={(e) => setUserEmail(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Recipient Options */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-3">
                  Additional Recipients
                </label>
                <div className="space-y-2">
                  {["VDC manager", "Planning manager", "1 more", "2 more"].map(
                    (option) => (
                      <label
                        key={option}
                        className="flex items-center space-x-3 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedRecipients.includes(option)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedRecipients([
                                ...selectedRecipients,
                                option,
                              ]);
                            } else {
                              setSelectedRecipients(
                                selectedRecipients.filter((r) => r !== option)
                              );
                            }
                          }}
                          className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500 focus:ring-2"
                        />
                        <span className="text-gray-300 text-sm">{option}</span>
                      </label>
                    )
                  )}
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <EmailButton
                    toEmails={[userEmail]}
                    subject={`Hillsdale Compliance Report - Analysis Results`}
                    body={generateReportSummary()}
                    pdfAttachment={pdfAttachment}
                    buttonText={
                      emailSent ? "Email Sent!" : "Send Email with PDF"
                    }
                    onSuccess={handleEmailSent}
                    onError={handleEmailError}
                    disabled={!userEmail || emailSent}
                    className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white font-medium py-3 px-6 rounded-lg transition-all duration-200 hover:from-blue-600 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>

                {onNavigateToCelebration && (
                  <button
                    onClick={() => {
                      onNavigateToCelebration();
                    }}
                    className="px-6 py-3 text-sm bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-medium rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl"
                  >
                    Celebrate Success
                  </button>
                )}
              </div>

              {emailSent && (
                <div className="flex justify-center">
                  <button
                    onClick={() => {
                      setEmailSent(false);
                      setUserEmail("");
                      setSelectedRecipients([]);
                    }}
                    className="px-6 py-3 text-sm text-gray-400 hover:text-white transition-colors border border-gray-600 rounded-lg hover:border-gray-500"
                  >
                    Send to different email
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
          <button
            onClick={handleClose}
            className="px-6 py-2 text-sm text-gray-400 hover:text-white transition-colors border border-gray-600 rounded-lg hover:border-gray-500"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
